import { useMutation, useQuery } from "react-query";
import { getReservation, getReservationBestTables, getReservations, getReservationsByDate, getReservationsByMonth, getReservationsCountByMonth, getReservationsFile, patchReservation, postReservation, postReservationChatThread } from "./requests";
import { notifyError, notifySuccess } from "@/toast-notifications";
import store from '@/store/configureStore';
import {
    UPDATE_RESERVATION_SUCCESS as UPDATE_RESERVATION_SUCCESS_MESSAGE,
} from "@/successes";
import {
    ADD_RESERVATION_ERROR,
    UPDATE_RESERVATION_ERROR as UPDATE_RESERVATION_ERROR_MESSAGE,
} from "@/errors";
import {
    ADD_RESERVATION_SUCCESS,
    UPDATE_RESERVATION_SUCCESS,
    UPDATE_RESERVATION_ERROR,
    UPDATE_RESERVATION_LOCALLY,
} from "@/store/actions/reservations";
import useFetchQuery from "@/hooks/useFetchQuery";
import useReduxStore from "@/hooks/useReduxStore";

import { useTranslation } from "react-i18next";

export const RESERVATIONS_KEY = "reservations";

export const buildReservationQueryKey = (id) => [RESERVATIONS_KEY, id];

export const buildReservationsFileQueryKey = (params) => [RESERVATIONS_KEY, "list", "file", params];

export function useGetReservation(id, options = {}) {
    const { onError, ...queryOptions } = options;
    return useQuery({
        ...queryOptions,
        queryKey: buildReservationQueryKey(id),
        queryFn: () => getReservation(id),
        onError: (error) => {
            if (onError) {
                onError(error);
            } else {
                notifyError("queries:reservations.error.get_one");
            }
        },
    });
}


export const useGetReservations = (filters, options = {}) =>
    useQuery({
        queryKey: [RESERVATIONS_KEY, "list", filters],
        queryFn: () => getReservations(filters),
        onError: () => {
            notifyError("queries:reservations.error.get_one");
        },
        ...options,
    });

export const useFetchReservationsFile = () =>
    useFetchQuery(
        (_context, args) => getReservationsFile(...args),
        buildReservationsFileQueryKey,
        {
            onError: () => {
                notifyError("queries:reservations.error.get_one");
            }
        }
    );

export const useFetchGetReservation = () =>
    useFetchQuery(
        (_context, args) => getReservation(...args),
        buildReservationQueryKey,
        {
            onError: () => {
                notifyError("queries:reservations.error.get_one");
            }
        }
    );

export const useGetReservationsByDate = (date, options = {}) =>
    useQuery(
        [RESERVATIONS_KEY, 'list', { date }],
        () => getReservationsByDate(date),
        {
            ...options,
            onError: () => {
                notifyError("queries:reservations.error.get_by_date");
            },
        },
    );

export const useGetReservationsByMonth = (month, year, options = {}) =>
    useQuery(
        [RESERVATIONS_KEY, 'list', { month, year }],
        () => getReservationsByMonth(month, year),
        {
            ...options,
            staleTime: Infinity,
            cacheTime: Infinity,
            onError: () => {
                notifyError("queries:reservations.error.get_by_month");
            },
        },
    );


export const RESERVATIONS_COUNTS_KEY = "reservations-counts";

export const useGetReservationsCountByMonth = (month, year, options = {}) =>
    useQuery(
        [RESERVATIONS_COUNTS_KEY, 'list', { month, year }],
        () => getReservationsCountByMonth(month, year),
        {
            ...options,
            staleTime: Infinity,
            cacheTime: Infinity,
            onError: () => {
                notifyError("queries:reservations.error.get_count");
            },
        },
    );


export const usePostReservation = (options = {}) =>
    useMutation(
        postReservation,
        {
            ...options,
            onSuccess: (reservation) => {
                store.dispatch({ type: ADD_RESERVATION_SUCCESS, reservation, loading: false, error: null, });
                notifySuccess("queries:reservations.success.post");
            },
            onError: (error) => {
                store.dispatch({ type: ADD_RESERVATION_ERROR, loading: false, error, });
                notifyError("queries:reservations.error.post");
            },
        }
    );

export function usePatchReservation(options = {}) {
    return useMutation(
        patchReservation,
        {
            ...options,
            onSuccess: (reservation) => {
                store.dispatch({ type: UPDATE_RESERVATION_SUCCESS, reservation, loading: false, error: null, });
                notifySuccess(UPDATE_RESERVATION_SUCCESS_MESSAGE);
            },
            onError: (error) => {
                store.dispatch({ type: UPDATE_RESERVATION_ERROR, loading: false, error, });
                notifyError(UPDATE_RESERVATION_ERROR_MESSAGE);
            },
        }
    )
}

export const RESERVATIONS_BEST_TABLES_KEY = "best-tables";

export function useFetchReservationBestTables(options = {}) {
    return useFetchQuery(
        (_context, [params]) => getReservationBestTables(params),
        (params) => [RESERVATIONS_KEY, RESERVATIONS_BEST_TABLES_KEY, params],
        options,
    );
}

export function usePostReservationChatThread(options = {}) {
    const { dispatch } = useReduxStore();
    const { t } = useTranslation();

    return useMutation(
        ({ reservationId, content }) => postReservationChatThread(reservationId, content),
        {
            ...options,
            onSuccess: (createdThread, variables, context) => {
                const { reservation } = variables;

                dispatch({
                    type: UPDATE_RESERVATION_LOCALLY,
                    reservation: {
                        ...reservation,
                        chat_thread: createdThread.id,
                    },
                });

                if (options.onSuccess) {
                    options.onSuccess(createdThread, variables, context);
                }
            },
            onError: (error, variables, context) => {
                notifyError(t("queries:reservations.error.post_chat_thread"));

                if (options.onError) {
                    options.onError(error, variables, context);
                }
            },
        }
    );
}


// WEBPACK FOOTER //
// ./src/queries/reservations/hooks.js